/**
 * ReportingStyleCard — Phase 1 RXF
 *
 * Radiologist-facing preference panel embedded in UserPreferencesPage.
 * Controls report length, impression style, recommendations, and formatting.
 * All selections auto-save with debounce; no explicit Save button needed.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import {
  updateReportingPreferences,
  selectReportingPrefs,
} from '../../store/userPreferencesSlice';

const REPORT_LENGTH_OPTIONS = [
  { value: 'concise', label: 'Concise', hint: 'Normal structures as single line; only abnormals expanded' },
  { value: 'detailed', label: 'Detailed', hint: 'Standard — balanced description for all structures' },
  { value: 'comprehensive', label: 'Comprehensive', hint: 'Full detail on every structure regardless of findings' },
];

const IMPRESSION_STYLE_OPTIONS = [
  { value: 'numbered_synthesis', label: 'Numbered Synthesis', hint: 'Numbered list with clinical correlation (default)' },
  { value: 'brief_bullets', label: 'Brief Bullets', hint: 'Short bullet points, minimal prose' },
  { value: 'narrative', label: 'Narrative Prose', hint: 'Continuous paragraph, no numbered list' },
];

const REC_STYLE_OPTIONS = [
  { value: 'none', label: 'None', hint: 'No follow-up recommendations included' },
  { value: 'standard', label: 'Standard', hint: 'Clinically indicated recommendations only (default)' },
  { value: 'aggressive', label: 'Include All', hint: 'All potentially relevant follow-ups listed' },
];

const ReportingStyleCard: React.FC = () => {
  const dispatch = useAppDispatch();
  const prefs = useAppSelector(selectReportingPrefs);

  const [saved, setSaved] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const autoSave = useCallback(
    (update: Parameters<typeof updateReportingPreferences>[0]) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        await dispatch(updateReportingPreferences(update));
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }, 600);
    },
    [dispatch],
  );

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        avatar={<AutoAwesomeIcon color="primary" />}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Reporting Style
            {prefs.saving ? (
              <CircularProgress size={14} />
            ) : saved ? (
              <Chip size="small" icon={<CheckIcon />} label="Saved" color="success" variant="outlined" />
            ) : null}
          </Box>
        }
        subheader="AI will adapt every report to match your personal style preferences"
      />
      <CardContent>
        {/* Report Length */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
            <Typography variant="subtitle2">Report Length</Typography>
            <Tooltip title="Controls how much detail is generated for normal structures">
              <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1}>
            {REPORT_LENGTH_OPTIONS.map((opt) => (
              <Tooltip key={opt.value} title={opt.hint} placement="top">
                <Chip
                  label={opt.label}
                  onClick={() => autoSave({ report_length: opt.value })}
                  color={prefs.reportLength === opt.value ? 'primary' : 'default'}
                  variant={prefs.reportLength === opt.value ? 'filled' : 'outlined'}
                  clickable
                />
              </Tooltip>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Impression Style */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
            <Typography variant="subtitle2">Impression Style</Typography>
            <Tooltip title="How findings are summarised in the IMPRESSION section">
              <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
            </Tooltip>
          </Stack>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <Select
              value={prefs.impressionStyle}
              onChange={(e) => autoSave({ impression_style: e.target.value })}
            >
              {IMPRESSION_STYLE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box>
                    <Typography variant="body2">{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{opt.hint}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Recommendations */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Recommendations</Typography>
          <Stack direction="row" spacing={1}>
            {REC_STYLE_OPTIONS.map((opt) => (
              <Tooltip key={opt.value} title={opt.hint} placement="top">
                <Chip
                  label={opt.label}
                  onClick={() => autoSave({ rec_style: opt.value })}
                  color={prefs.recStyle === opt.value ? 'primary' : 'default'}
                  variant={prefs.recStyle === opt.value ? 'filled' : 'outlined'}
                  clickable
                />
              </Tooltip>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Formatting options */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Formatting</Typography>

          <FormControlLabel
            control={
              <Switch
                checked={prefs.boldPositives}
                onChange={(e) => autoSave({ bold_positives: e.target.checked })}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2">Bold positive / abnormal findings</Typography>
                <Typography variant="caption" color="text.secondary">
                  All clinically significant positives are **bolded** in the FINDINGS section
                </Typography>
              </Box>
            }
          />

          <Box sx={{ mt: 2, maxWidth: 320 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <Typography variant="body2">Max impression points</Typography>
              <Tooltip title="Set to 0 for no limit. AI will cap the numbered impression list at this many items.">
                <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
              </Tooltip>
            </Stack>
            <Slider
              value={prefs.maxImpressionPoints}
              onChange={(_, val) =>
                autoSave({ max_impression_points: val as number })
              }
              min={0}
              max={10}
              step={1}
              marks={[
                { value: 0, label: 'No limit' },
                { value: 5, label: '5' },
                { value: 10, label: '10' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => (v === 0 ? '∞' : `${v}`)}
              size="small"
            />
          </Box>
        </Box>

        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Preferences are applied to every new report automatically. They take effect after the next
            report is generated — no restart required.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReportingStyleCard;
